import * as React from 'react';
import {animate, view, createViewConfig} from '@sail/engine';
import type {View} from '../../view';

import {FocusScope} from '../layers/FocusScope';
import {Button, ButtonConfig} from '../Button';
import {tokens} from '../../tokens';
import {useUiConfig} from '../../providers/UiConfigProvider';

export type DrawerConfirmationDialogProps = View.IntrinsicElement<
  'div',
  {
    /**
     * All the strings we need
     */
    messages: {
      title: string;
      description: string;
      cancelAction: string;
      exitAction: string;
    };
    /**
     * The function to be called when the user confirms exiting
     */
    onClose: () => void;
    /**
     * The function to be called when the user cancels exiting
     */
    onCancel: () => void;

    subviews?: View.Subviews<{
      wash: 'div';
      heading: 'div';
      content: 'div';
      footer: 'div';
    }>;
  }
>;

export const DrawerConfirmationDialogConfig = createViewConfig({
  props: {} as DrawerConfirmationDialogProps,
  name: 'DrawerConfirmationDialog',
});

export const DrawerConfirmationDialog =
  DrawerConfirmationDialogConfig.createView(
    ({messages, onCancel, onClose, subviews, ...props}) => {
      const isSmallScreen = useUiConfig()?.isSmallScreen;
      return (
        <view.div
          css={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0}}
          uses={[
            animate.inout({
              duration: 150,
              opacity: 0,
              easing: 'ease-in-out',
            }),
          ]}
        >
          <view.div inherits={subviews.wash} />

          <view.div
            css={{
              stack: 'x',
              alignX: 'center',
              alignY: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <view.div {...props} role="dialog">
              <FocusScope contain autoFocus restoreFocus>
                <view.h2 inherits={subviews.heading}>{messages.title}</view.h2>

                <view.section inherits={subviews.content}>
                  {messages.description}
                </view.section>

                <view.div
                  uses={[
                    isSmallScreen
                      ? ButtonConfig.customize({defaults: {size: 'large'}})
                      : null,
                  ]}
                  inherits={subviews.footer}
                >
                  <Button type="secondary" onPress={onCancel}>
                    {messages.cancelAction}
                  </Button>
                  <Button type="destructive" onPress={onClose}>
                    {messages.exitAction}
                  </Button>
                </view.div>
              </FocusScope>
            </view.div>
          </view.div>
        </view.div>
      );
    },
    {
      css: {
        stack: 'y',
        gap: 'space.50',
        alignY: 'center',
        maxWidth: '400px',
        backgroundColor: 'surface',
        boxShadow: `0 0 50px 40px ${tokens.color.background.surface}`,
        borderRadius: '100%',
        margin: 'space.500',
      },

      subviews: {
        wash: {
          css: {
            backgroundColor: 'surface',
            opacity: 0.75,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        },
        heading: {
          css: {font: 'heading.small', marginBottom: 0},
        },
        content: {
          css: {font: 'body.small'},
        },
        footer: {
          css: {stack: 'x', gap: 'small', marginTop: 'space.150'},
        },
      },
    },
  );
